import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { Route, Routes, useLocation, useNavigate, Navigate, matchPath } from 'react-router-dom';

import {
    Title,
    Legend,
    Tooltip,
    BarElement,
    LinearScale,
    CategoryScale,
    Chart as ChartJS,
} from 'chart.js';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Toolbar from '@mui/material/Toolbar';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Close from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import IconButton from '@mui/material/IconButton';
import CssBaseline from '@mui/material/CssBaseline';
import { styled, ThemeProvider } from '@mui/material/styles';
import { Divider, Grid, useMediaQuery } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import './App.scss';

import {
    RESET,
    LOGOUT,
    GET_AUTH_ME,
    UPDATE_COMPANY_ERROR,
    UPDATE_COMPANY_PENDING,
    UPDATE_COMPANY_SUCCESS,
} from './store/actions';

import Sidebar from './shared/Sidebar';
import HotJarInsert from 'HotJarInsert';
import { useAuth } from './hooks/useAuth';
import { isObjectEmpty } from './utils/helpers';
import { Typography, Menu, Switch } from './shared';
import UpgradeBanner from 'components/UpgradeBanner';
import { lightTheme, darkTheme } from './assets/theme';
import { SwipeableFromLeftDrawer } from './components';
import { useDispatch, useSelector } from 'react-redux';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useThemeToggle } from './hooks/useThemeToggle';

import TourButton from 'components/Onboarding/Tour/TourButton';
import { useAuthService } from './store/services/auth.service';
import { ReactComponent as Logout } from './assets/icons/Logout.svg';
import { useCompanyService } from './store/services/company.service';

import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import { ReactComponent as DarkMode } from './assets/icons/DarkMode.svg';

import HistoryDropDown from 'components/History/HistoryDropDown';
import HistoryActionModal from 'components/History/HistoryActionModal';
import InviteUserLinkModal from 'components/Rewardful/InviteUserLinkModal';
import { PRIVATE_ROUTES, PUBLIC_PATHNAMES, PUBLIC_ROUTES } from './routes';
import { ReactComponent as UserProfile } from './assets/icons/UserProfile.svg';
import { resetAllTours, setTutorialPreference } from 'store/slices/tour.slice';
import { ReactComponent as DarkModeIcon } from './assets/icons/DarkModeToggle.svg';
import OnBoardingTutorialModal from 'components/Onboarding/OnBoardingTutorialModal';
import { ReactComponent as UserProfileDarkMode } from './assets/icons/UserProfileDarkMode.svg';
import NotificationsDropdown from 'components/Notifications/NotificationsDropdown';

const drawerWidth = 275;

const makeStyles = ({ isDrawerOpen, isScreenLowerThan1024, themeMode, isOnboarding }) => ({
    toolBar: {
        transition: 'ease-in-out 300ms',
        '&. MuiToolbar-root': {
            minHeight: 53,
        },
        display: 'flex',
        alignContent: 'center',
        padding: isScreenLowerThan1024 ? '20px' : '20px 40px 20px 10px',
        left: isDrawerOpen ? (isScreenLowerThan1024 ? 0 : 230) : isScreenLowerThan1024 ? 0 : 64,
        backgroundColor: (theme) => theme.backgroundColor.primary,
        width: isScreenLowerThan1024 ? `100%` : `calc(100vw - ${isDrawerOpen ? '215px' : '50px'})`,
        borderBottom: (theme) =>
            theme.palette.mode === 'light' ? '1px solid #E5E6EB' : '1px solid #394150',
    },
    drawerPaper: {
        borderRight: (theme) =>
            theme.palette.mode === 'light' ? '1px solid #E5E6EB' : '1px solid #394150',
        overflow: 'auto',
        background: (theme) => theme.backgroundColor.primary,
    },
    notificationCount: {
        display: 'flex',
        width: '24px',
        height: '24px',
        padding: '8px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        borderRadius: '1000px',
        background: '#F75555',
    },
    content: {
        position: 'relative',
        backgroundColor: (theme) => theme.backgroundColor.secondary,
        height: isOnboarding ? 'auto' : '100vh',
        overflow: 'auto !important',
        paddingTop: isOnboarding ? '0' : '85px',
        width: isOnboarding ? '100%' : '100%',
        '&::-webkit-scrollbar': {
            width: '0.4rem',
        },
        '&::-webkit-scrollbar-track': {
            boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
            webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
        },
        '&::-webkit-scrollbar-thumb': {
            margin: 1,
            borderRadius: '8px',
            backgroundColor: 'rgba(0,0,0,.1)',
        },
    },
    historyDropDown: {
        display: 'flex',
        alignItems: 'center',
    },

    notificationMenuStyle: {
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        width: '300px',
        borderBottom: (theme) => `1px solid ${theme.borderColor.inputField}`,
        background: (theme) => theme.backgroundColor.primary,
        p: 1,
        '& ul': {
            height: '500px',
            overflowY: 'auto',
            p: 0,
            m: 0,
            width: '100%',
            '& li': {
                whiteSpace: 'nowrap', // Prevents text from wrapping to the next line
                overflow: 'hidden', // Hides any text that overflows the container
                textOverflow: 'ellipsis', // Displays an ellipsis (...) when text is clipped
                '&:hover': {
                    borderRadius: '12px',
                },
            },

            '&::-webkit-scrollbar': {
                width: '0.8rem',
            },
            '&::-webkit-scrollbar-track': {
                boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
                webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
            },
            '&::-webkit-scrollbar-thumb': {
                margin: 1,
                borderRadius: '8px',
                backgroundColor: 'rgba(0,0,0,.1)',
            },
        },
    },
});

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open, isScreenLowerThan1024 }) => ({
    width: isScreenLowerThan1024 ? 0 : drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
        ...closedMixin(theme, isScreenLowerThan1024),
        '& .MuiDrawer-paper': closedMixin(theme, isScreenLowerThan1024),
    }),
}));

const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme, isScreenLowerThan1024) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: isScreenLowerThan1024 ? 0 : `calc(${theme.spacing(7)} + 5px)`,
    [theme.breakpoints.up('sm')]: {
        width: isScreenLowerThan1024 ? 0 : `calc(${theme.spacing(8)} + 5px)`,
    },
});

const App = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { logout, user } = useAuth();
    const [open, setOpen] = useState(false);
    const [showInviteLink, setShowInviteLink] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [billingData, setBillingData] = useState(null);
    const [billingLoading, setBillingLoading] = useState(false);

    const { getLoggedInUserDetails } = useAuthService();
    const { getUserAssociatedCompany, getCompanyBilling } = useCompanyService();
    const { themeMode, toggleTheme } = useThemeToggle();
    const isDarkMode = themeMode === 'dark' ? true : false;
    const [activeMenu, setActiveMenu] = useState('');

    const { token } = useSelector((state) => state.auth);
    const { data: companyData } = useSelector((state) => state.company);

    const isPublicRoute = PUBLIC_ROUTES.some((route) =>
        matchPath({ path: route.path, end: true }, location.pathname),
    );

    const getCurrentPathLabel = () => {
        const path = location.pathname;

        // Skip root path as it's just a redirect
        if (path === '/') {
            return '';
        }

        // Find matching route
        const route = PRIVATE_ROUTES.find((r) => {
            if (r.element?.type === Navigate) return false;
            if (!r.label) return false;
            const routePath = r.path.replace('/*', '').replace('/:section', '');
            return path.startsWith(routePath);
        });

        return route?.label || '';
    };

    const currentPath = getCurrentPathLabel();

    const isScreenLowerThan1024 = useMediaQuery('(max-width: 1025px)');

    const styles = makeStyles({
        isDrawerOpen: open,
        isScreenLowerThan1024,
        themeMode,
        isOnboarding: currentPath === 'onboarding',
    });

    const getAuthMe = useCallback(() => {
        dispatch(GET_AUTH_ME({ getLoggedInUserDetails }));
    }, [dispatch, getLoggedInUserDetails]);

    useEffect(() => {
        if (token && isObjectEmpty(user.data) && !user.loading) {
            getAuthMe();
        }
    }, [token, user.data, user.loading, getAuthMe]);

    useEffect(() => {
        if (!token) {
            if (!isPublicRoute) {
                navigate('/login', { replace: true });
            }
            return;
        }

        if (!user.loading && !isObjectEmpty(user.data)) {
            if (isPublicRoute) {
                if (user.data.is_onboarded) {
                    navigate('/purchases/for-review', { replace: true });
                } else {
                    navigate('/onboarding', { replace: true });
                }
            }
        }

        if (!user.loading && user.error) {
            navigate('/login', { replace: true });
        }
    }, [token, user.data, user.loading, user.error, isPublicRoute, navigate]);

    useEffect(() => {
        if (token && user?.data?.is_active) {
            dispatch(UPDATE_COMPANY_PENDING({ loading: true }));

            getUserAssociatedCompany()
                .then((data) => {
                    const companyData = data[0];
                    dispatch(
                        UPDATE_COMPANY_SUCCESS({
                            data: {
                                id: companyData.id,
                                name: companyData.name,
                                timezone: {
                                    label: companyData.timezone,
                                    value: companyData.timezone,
                                },
                                industry: {
                                    label: companyData.industry_name,
                                    value: companyData.industry,
                                },
                                entity: {
                                    label: companyData.entity,
                                    value: companyData.entity,
                                },
                                address: companyData.address,
                                email: companyData.email,
                                stripeTrialExpiry: companyData.subscription_trial_end,
                                subscription_active: companyData.subscription_active,
                                stripeStatus: companyData.stripe_status,
                                parking_tickets_enabled: companyData.parking_tickets_enabled,
                                is_invoice_reconciler: companyData.is_invoice_reconciler,
                                is_purchase_reconciler: companyData.is_purchase_reconciler,
                            },
                        }),
                    );
                })

                .catch((err) => {
                    dispatch(UPDATE_COMPANY_ERROR({ err }));
                });
        }
    }, [getUserAssociatedCompany, dispatch, token, user?.data?.is_active]);

    const fetchBillingData = async () => {
        if (!companyData?.id) return;

        try {
            setBillingLoading(true);
            const response = await getCompanyBilling(companyData.id);
            setBillingData(response.data);
        } catch (error) {
            console.error('Error fetching billing data:', error);
        } finally {
            setBillingLoading(false);
        }
    };

    const handleDrawerToggle = () => {
        setOpen(!open);
    };

    const handleDrawerClose = () => {
        setOpen(false);
    };

    const handleMenuClick = (e, option) => {
        if (option.path.startsWith('https')) {
            window.open(option.path, '_blank');
            return;
        }
        if (option.path === '/darkMode') {
            e?.stopPropagation();
            return;
        }
        if (option.path === '/logout') {
            logout();
            return;
        } else {
            setActiveMenu(option.name);
            navigate(option.path);
        }
    };

    const handleClick = async (event) => {
        setAnchorEl(event.currentTarget);
        await fetchBillingData();
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const renderedPrivateRoutes = PRIVATE_ROUTES.map((route, index) => (
        <Route key={index} path={route.path} element={route.element} />
    ));

    const renderedPublicRoutes = PUBLIC_ROUTES.map((route, index) => (
        <Route key={index} path={route.path} element={route.element} />
    ));

    const handleDarkMode = (e) => {
        e.stopPropagation();
        toggleTheme(e);
    };

    const handleLogout = () => {
        dispatch(RESET());
        dispatch(resetAllTours());
        localStorage.clear();
        window.localStorage.clear();
        window.sessionStorage.clear();

        logout();
    };

    const inviteUserLink = () => {
        setShowInviteLink(true);
    };

    useEffect(() => {
        if (user?.data?.wants_tutorial === false) {
            dispatch(setTutorialPreference({ preference: 'skipped' }));
        } else if (user?.data?.wants_tutorial === true) {
            dispatch(setTutorialPreference({ preference: 'enabled' }));
        }
    }, [user?.data?.wants_tutorial, dispatch]);

    return (
        <ThemeProvider theme={themeMode === 'light' ? lightTheme : darkTheme}>
            <CssBaseline />
            <HotJarInsert />

            {user.data ? (
                <Fragment>
                    <Box
                        sx={{
                            display: 'flex',
                        }}
                        className="circler-container"
                    >
                        {currentPath !== 'onboarding' && currentPath !== 'Subscriptions' && (
                            <Box>
                                <AppBar
                                    elevation={0}
                                    position="fixed"
                                    sx={{
                                        backgroundColor: (theme) => theme.backgroundColor.primary,
                                        zIndex: (theme) => theme.zIndex.drawer - 1,
                                    }}
                                >
                                    <Toolbar disableGutters sx={styles.toolBar}>
                                        <Grid
                                            container
                                            spacing={2}
                                            justifyContent={'space-between'}
                                        >
                                            <Grid
                                                item
                                                sx={{
                                                    cursor: 'pointer',
                                                    color: (theme) => theme.textColor.primary,
                                                }}
                                                xs={8}
                                                md={4}
                                                lg={4}
                                                display={'flex'}
                                                alignItems={'center'}
                                                justifyContent={'flex-start'}
                                                ml={isScreenLowerThan1024 ? 0 : 4}
                                            >
                                                {isScreenLowerThan1024 ? (
                                                    <Box display={'flex'} alignItems={'center'}>
                                                        <IconButton
                                                            color="red !important"
                                                            onClick={handleDrawerToggle}
                                                            sx={{
                                                                p: '8px 0',
                                                                marginRight: '8px',
                                                            }}
                                                        >
                                                            {open ? (
                                                                <Close
                                                                    sx={{
                                                                        color: (theme) =>
                                                                            theme.textColor.primary,
                                                                    }}
                                                                />
                                                            ) : (
                                                                <MenuIcon
                                                                    sx={{
                                                                        color: (theme) =>
                                                                            theme.textColor.dark,
                                                                    }}
                                                                />
                                                            )}
                                                        </IconButton>
                                                        <Typography
                                                            text={currentPath}
                                                            sx={{
                                                                fontSize: isScreenLowerThan1024
                                                                    ? '20px'
                                                                    : '24px',
                                                                fontWeight: 700,
                                                                textTransform: 'capitalize',
                                                                color: (theme) =>
                                                                    theme.textColor.primary,
                                                                display: 'block',
                                                                width: '100%',
                                                            }}
                                                        />
                                                    </Box>
                                                ) : (
                                                    <Typography
                                                        text={currentPath}
                                                        sx={{
                                                            paddingLeft: '1rem',
                                                            fontSize: '24px',
                                                            fontWeight: 700,
                                                            textTransform: 'capitalize',
                                                            display: 'block',
                                                            width: '100%',
                                                            color: (theme) =>
                                                                theme.textColor.primary,
                                                        }}
                                                    />
                                                )}
                                            </Grid>

                                            <Grid
                                                item
                                                xs={isScreenLowerThan1024 ? 4 : 2}
                                                md={7}
                                                lg={7}
                                                container
                                                spacing={2}
                                                display={'flex'}
                                                justifyContent={'flex-end'}
                                            >
                                                {isScreenLowerThan1024 ? (
                                                    <Grid item>
                                                        {companyData &&
                                                            companyData?.stripeStatus !==
                                                                'active' && (
                                                                <UpgradeBanner
                                                                    isDarkMode={isDarkMode}
                                                                />
                                                            )}
                                                    </Grid>
                                                ) : (
                                                    <Fragment>
                                                        <Grid
                                                            item
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                            }}
                                                        ></Grid>
                                                        {['Transaction', 'History'].includes(
                                                            currentPath,
                                                        ) && (
                                                            <Grid
                                                                item
                                                                sx={{
                                                                    display: isScreenLowerThan1024
                                                                        ? 'none'
                                                                        : {
                                                                              md: 'flex',
                                                                          },
                                                                    alignItems: 'center',
                                                                }}
                                                            ></Grid>
                                                        )}
                                                        <Grid item>
                                                            {companyData &&
                                                                companyData?.stripeStatus !==
                                                                    'active' && (
                                                                    <UpgradeBanner
                                                                        isDarkMode={isDarkMode}
                                                                    />
                                                                )}
                                                        </Grid>
                                                        <Grid item sx={styles.historyDropDown}>
                                                            <HistoryDropDown
                                                                onSeeAllClick={() =>
                                                                    setShowHistoryModal(true)
                                                                }
                                                            />
                                                        </Grid>

                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            <NotificationsDropdown />
                                                        </Box>

                                                        <Grid item>
                                                            <Box height={'100%'}>
                                                                <Divider
                                                                    variant="fullWidth"
                                                                    orientation="vertical"
                                                                    sx={{
                                                                        background: (theme) =>
                                                                            theme.palette.mode ===
                                                                            'dark'
                                                                                ? '#394150'
                                                                                : '#E5E6EB',
                                                                        borderRightWidth: '1px',
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Grid>
                                                        <Grid
                                                            item
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                            <Avatar
                                                                alt={user.data.first_name}
                                                                src={`https://app.circler.io/${user.data.photo_url}`}
                                                                onClick={handleClick}
                                                            />
                                                        </Grid>
                                                    </Fragment>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </Toolbar>
                                </AppBar>

                                {isScreenLowerThan1024 ? (
                                    <SwipeableFromLeftDrawer
                                        isDrawerOpen={open}
                                        activeMen={activeMenu}
                                        currentPath={currentPath}
                                        handleMenuClick={handleMenuClick}
                                        notificationStyle={styles.notificationCount}
                                        setOpen={setOpen}
                                        isScreenLowerThan1024={isScreenLowerThan1024}
                                        isDarkMode={isDarkMode}
                                    />
                                ) : (
                                    <Drawer
                                        open={open}
                                        variant="permanent"
                                        PaperProps={{
                                            sx: styles.drawerPaper,
                                        }}
                                        isScreenLowerThan1024={isScreenLowerThan1024}
                                    >
                                        <Sidebar
                                            handleMenuClick={handleMenuClick}
                                            isDrawerOpen={open}
                                            activeMenu={activeMenu}
                                            setIsDrawerOpen={setOpen}
                                            currentPath={currentPath}
                                            notificationStyle={styles.notificationCount}
                                            isScreenLowerThan1024={isScreenLowerThan1024}
                                        />
                                    </Drawer>
                                )}
                                {!isScreenLowerThan1024 &&
                                    (open ? (
                                        <IconButton
                                            color="primary"
                                            aria-label="open drawer"
                                            component="label"
                                            edge="start"
                                            onClick={handleDrawerClose}
                                            size="small"
                                            sx={{
                                                position: 'fixed',
                                                top: '28px',
                                                zIndex: 1200,
                                                left: '248px',
                                                background: (theme) =>
                                                    theme.backgroundColor.primary,
                                                border: (theme) =>
                                                    `1px solid ${theme.borderColor.inputField}`,
                                                color: '#6C727F',
                                                padding: '2px',
                                                '& :hover': {
                                                    background: (theme) =>
                                                        theme.backgroundColor.primary,
                                                    borderRadius: '50%',
                                                },
                                            }}
                                        >
                                            <ChevronLeftIcon />
                                        </IconButton>
                                    ) : (
                                        <IconButton
                                            onClick={handleDrawerToggle}
                                            color="primary"
                                            aria-label="open drawer"
                                            component="label"
                                            edge="start"
                                            sx={{
                                                position: 'fixed',
                                                top: '28px',
                                                left: '66px',
                                                zIndex: 1200,
                                                background: (theme) =>
                                                    theme.backgroundColor.primary,
                                                border: (theme) =>
                                                    `1px solid ${theme.borderColor.inputField}`,
                                                color: '#6C727F',
                                                padding: '2px',
                                                '&:hover': {
                                                    background: (theme) =>
                                                        theme.backgroundColor.primary,
                                                    borderRadius: '50%',
                                                },
                                            }}
                                        >
                                            <ChevronRightIcon />
                                        </IconButton>
                                    ))}
                            </Box>
                        )}

                        <Box component="main" sx={styles.content}>
                            <Routes>{renderedPrivateRoutes}</Routes>
                        </Box>
                    </Box>

                    <Menu
                        items={[
                            ...(companyData?.is_purchase_reconciler
                                ? [
                                      {
                                          name: (
                                              <Box
                                                  sx={{ width: '100%', minWidth: '220px' }}
                                                  onClick={() => navigate('/profile/billing')}
                                              >
                                                  <Typography
                                                      text={`Emails Sent: ${
                                                          billingLoading
                                                              ? 'Loading...'
                                                              : `${(
                                                                    billingData?.email_used || 0
                                                                ).toLocaleString()} / ${(
                                                                    billingData?.email_limit || 0
                                                                ).toLocaleString()}`
                                                      }`}
                                                      customStyles={{
                                                          fontSize: '12px',
                                                          color: (theme) =>
                                                              theme.palette.mode === 'dark'
                                                                  ? '#9ea3ae'
                                                                  : '#6C727F',
                                                          mb: 1.25,
                                                          display: 'block',
                                                          whiteSpace: 'nowrap',
                                                          overflow: 'hidden',
                                                          textOverflow: 'ellipsis',
                                                      }}
                                                  />
                                                  <Box
                                                      sx={{
                                                          display: 'flex',
                                                          gap: 0.75,
                                                          mb: 1.25,
                                                          flexWrap: 'wrap',
                                                      }}
                                                  >
                                                      <Box
                                                          sx={{
                                                              bgcolor: (theme) =>
                                                                  theme.palette.mode === 'dark'
                                                                      ? '#2D3748'
                                                                      : '#F3F4F6',
                                                              borderRadius: '12px',
                                                              px: 1.25,
                                                              py: 0.25,
                                                              fontSize: '11px',
                                                              color: (theme) =>
                                                                  theme.palette.mode === 'dark'
                                                                      ? '#E2E8F0'
                                                                      : '#4B5563',
                                                              whiteSpace: 'nowrap',
                                                          }}
                                                      >
                                                          {billingLoading
                                                              ? 'Loading...'
                                                              : `Purchase Volume: ${(
                                                                    billingData?.purchase_volume_used ||
                                                                    0
                                                                ).toLocaleString()} / ${
                                                                    billingData?.purchase_volume_limit ===
                                                                    null
                                                                        ? 'Unlimited'
                                                                        : (
                                                                              billingData?.purchase_volume_limit ||
                                                                              0
                                                                          ).toLocaleString()
                                                                }`}
                                                      </Box>
                                                  </Box>
                                                  <Typography
                                                      text="Upgrade Plan"
                                                      sx={{
                                                          color: '#3B82F6',
                                                          fontSize: '12px',
                                                          fontWeight: 500,
                                                          cursor: 'pointer',
                                                          '&:hover': {
                                                              textDecoration: 'underline',
                                                          },
                                                          display: 'inline-block',
                                                      }}
                                                      onClick={() => navigate('/profile/billing')}
                                                  />
                                              </Box>
                                          ),
                                          isText: true,
                                          disabled: true,
                                          onClick: () => {
                                              navigate('/profile/billing');
                                              setActiveMenu('');
                                          },
                                      },
                                  ]
                                : []),
                            {
                                name: (
                                    <Grid container spacing={2}>
                                        <Grid item>
                                            {isDarkMode ? <UserProfileDarkMode /> : <UserProfile />}
                                        </Grid>
                                        <Grid item>
                                            <Typography
                                                text={'My Profile'}
                                                customStyles={{
                                                    fontSize: 14,
                                                    fontWeight: 500,
                                                    color: (theme) =>
                                                        theme.palette.mode === 'dark'
                                                            ? '#F9FAFB'
                                                            : '#6C727F',
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                ),
                                onClick: () => {
                                    navigate('/profile');
                                    setActiveMenu('');
                                },
                            },
                            {
                                name: (
                                    <Grid container spacing={2}>
                                        <Grid item>
                                            <PointOfSaleIcon
                                                sx={{
                                                    color: (theme) =>
                                                        theme.palette.mode === 'dark'
                                                            ? '#9ea3ae'
                                                            : '#6C727F',
                                                }}
                                            />
                                        </Grid>
                                        <Grid item>
                                            <Typography
                                                text={'Point of Sale'}
                                                customStyles={{
                                                    fontSize: 14,
                                                    fontWeight: 500,
                                                    color: (theme) =>
                                                        theme.palette.mode === 'dark'
                                                            ? '#F9FAFB'
                                                            : '#6C727F',
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                ),
                                onClick: () => {
                                    navigate('/profile/point-of-sale');
                                    setActiveMenu('');
                                },
                            },
                            {
                                name: (
                                    <Grid
                                        container
                                        spacing={2}
                                        display={'flex'}
                                        alignItems={'center'}
                                        justifyContent={'space-between'}
                                    >
                                        <Grid item xs={2}>
                                            {isDarkMode ? <DarkModeIcon /> : <DarkMode />}
                                        </Grid>
                                        <Grid item xs={6} display={'flex'}>
                                            <Typography
                                                text={'Dark Mode'}
                                                customStyles={{
                                                    fontSize: 14,
                                                    fontWeight: 500,
                                                    color: (theme) =>
                                                        theme.palette.mode === 'dark'
                                                            ? '#F9FAFB'
                                                            : '#6C727F',
                                                }}
                                            />
                                        </Grid>
                                        <Grid
                                            item
                                            xs={4}
                                            display={'flex'}
                                            justifyItems={'flex-start'}
                                        >
                                            <Switch onClick={handleDarkMode} checked={isDarkMode} />
                                        </Grid>
                                    </Grid>
                                ),
                                onClick: () => {},
                            },
                            {
                                name: (
                                    <Grid container spacing={2}>
                                        <Grid item>
                                            <Logout />
                                        </Grid>
                                        <Grid item>
                                            <Typography
                                                text={'Log out'}
                                                customStyles={{
                                                    color: (theme) => theme.textColor.warning,
                                                    fontSize: 14,
                                                    fontWeight: 500,
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                ),
                                onClick: () => handleLogout(),
                                showDivider: true,
                            },
                            {
                                name: (
                                    <Grid container>
                                        <Grid item>
                                            <Typography
                                                text={'Privacy Policy'}
                                                customStyles={{
                                                    fontSize: 14,
                                                    fontWeight: 500,
                                                    color: (theme) =>
                                                        theme.palette.mode === 'dark'
                                                            ? '#F9FAFB'
                                                            : '#6C727F',
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                ),
                                onClick: () => {
                                    window.open(
                                        'https://www.termsfeed.com/live/c453ce2a-a7f6-4fd4-8956-efc53c6f2510',
                                        '_blank',
                                    );
                                },
                            },
                            {
                                name: (
                                    <Grid container>
                                        <Grid item>
                                            <Typography
                                                text={'Terms and Conditions'}
                                                customStyles={{
                                                    fontSize: 14,
                                                    fontWeight: 500,
                                                    color: (theme) =>
                                                        theme.palette.mode === 'dark'
                                                            ? '#F9FAFB'
                                                            : '#6C727F',
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                ),
                                onClick: () => {
                                    window.open(
                                        'https://www.termsfeed.com/live/2c8418f9-830b-4917-95c9-1a30f68a8c7a',
                                        '_blank',
                                    );
                                },
                            },
                            {
                                name: (
                                    <Grid container>
                                        <Grid item>
                                            <Typography
                                                text={'Help Center'}
                                                customStyles={{
                                                    fontSize: 14,
                                                    fontWeight: 500,
                                                    color: (theme) =>
                                                        theme.palette.mode === 'dark'
                                                            ? '#F9FAFB'
                                                            : '#6C727F',
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                ),
                                onClick: () => {
                                    window.open('https://circlerhelp.blogspot.com/', '_blank');
                                },
                            },
                            {
                                name: (
                                    <Grid container>
                                        <Grid item>
                                            <Typography
                                                text={'About Us'}
                                                customStyles={{
                                                    fontSize: 14,
                                                    fontWeight: 500,
                                                    color: (theme) =>
                                                        theme.palette.mode === 'dark'
                                                            ? '#F9FAFB'
                                                            : '#6C727F',
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                ),
                                onClick: () => {
                                    window.open('https://circler.io/', '_blank');
                                },
                            },

                            {
                                name: (
                                    <Grid container>
                                        <Grid item>
                                            <Typography
                                                text={'Invite'}
                                                customStyles={{
                                                    fontSize: 14,
                                                    fontWeight: 500,
                                                    color: (theme) =>
                                                        theme.palette.mode === 'dark'
                                                            ? '#F9FAFB'
                                                            : '#6C727F',
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                ),
                                onClick: () => {
                                    inviteUserLink();
                                },
                            },
                        ]}
                        anchorEl={anchorEl}
                        handleClose={handleCloseMenu}
                        customStyles={{
                            width: '254px',
                            p: 1,
                        }}
                    />

                    {showInviteLink && (
                        <InviteUserLinkModal
                            open={showInviteLink}
                            companyId={companyData?.id}
                            handleCloseModal={() => setShowInviteLink(false)}
                            handleExclusionRuleCreationSuccess={() => {}}
                        />
                    )}

                    {!location.pathname.includes('/subscriptions') &&
                        !location.pathname.includes('/onboarding') && <TourButton />}
                    {user?.data?.wants_tutorial && <OnBoardingTutorialModal />}

                    {showHistoryModal && (
                        <HistoryActionModal
                            open={showHistoryModal}
                            handleClose={() => setShowHistoryModal(false)}
                        />
                    )}
                </Fragment>
            ) : (
                <Routes>{renderedPublicRoutes}</Routes>
            )}
        </ThemeProvider>
    );
};

export default App;
